'use client'

import { useCallback } from 'react'
import { useSession } from 'next-auth/react'

import {
  mapSelectedSlotsToPayload,
  TMSDeliverySlotsArraySlot
} from 'modules/delivery'
import toast from 'shared/ui/utils/toast'

import useCartStore from '../../../store/cartStore'

interface UseReservationSubmissionParams {
  allSelectedSlots: TMSDeliverySlotsArraySlot[]
  rawSlotsForSelection: TMSDeliverySlotsArraySlot[]
  dateWithSelection: string | null
  setSelectedDeliverySlots: (
    slotsOrUpdater:
      | TMSDeliverySlotsArraySlot[]
      | ((prevSlots: TMSDeliverySlotsArraySlot[]) => TMSDeliverySlotsArraySlot[])
  ) => void
  clearCache: () => void
  handleEndReservation: () => void
}

/**
 * Custom hook for handling reservation submission logic
 * Manages the process of confirming and submitting delivery slot reservations
 */
export const useReservationSubmission = ({
  allSelectedSlots,
  rawSlotsForSelection,
  dateWithSelection,
  setSelectedDeliverySlots,
  clearCache,
  handleEndReservation
}: UseReservationSubmissionParams) => {
  const { TMSReserveSlotToCart } = useCartStore()
  const session = useSession()

  const handleConfirmReservation = useCallback(async (): Promise<boolean> => {
    if (!dateWithSelection) return false

    const selectedSlots = allSelectedSlots.map(slot =>
      rawSlotsForSelection.find(
        rawDeliverySlot => rawDeliverySlot.time === slot.time
      )
    )


    const payload = mapSelectedSlotsToPayload(selectedSlots)

    if (!payload) {
      toast.error('Wystąpił błąd podczas rezerwowania terminu dostawy')

      return false
    }

    try {
      await TMSReserveSlotToCart(payload, session)
      toast.success('Zarezerwowano termin dostawy')
      setSelectedDeliverySlots([])
      clearCache()
      handleEndReservation()

      return true
    } catch (error) {
      console.error(error)
      toast.error('Wystąpił błąd podczas rezerwowania terminu dostawy')

      return false
    }
  }, [
    dateWithSelection,
    allSelectedSlots,
    rawSlotsForSelection,
    TMSReserveSlotToCart,
    session,
    setSelectedDeliverySlots,
    clearCache,
    handleEndReservation
  ])

  return { handleConfirmReservation } as const
} 